import { createPortal } from "react-dom";
import { useEffect, useRef } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { useOneShotModal } from "./useOneShotModal";

/**
 * One-shot confirm dialog — a peer primitive to `CoachModal`, not
 * a wrapper. Replaces every `window.confirm()` in the app per the
 * no-native-browser-chrome rule.
 *
 * Used by:
 *   - Coach Review: clear-drawing, template-overwrite,
 *     Apply-Formation (M7.5 B4 / B5 / B6).
 *   - Future: any other destructive or replace-style action that
 *     used to use `window.confirm`.
 *
 * Sized smaller than `CoachModal` (max-w-sm, no scrollable body)
 * and built with a single button row (confirm + cancel) — the
 * form-modal shape doesn't fit a yes/no prompt. The two primitives
 * share `useOneShotModal` for Escape handling; the M6a `CoachModal`
 * gets the same hook by adoption if/when a polish pass touches it.
 *
 * `danger` flips the confirm button to a destructive variant —
 * use it for clear-drawing / Apply-Formation (the legacy
 * `confirmAction({ danger: true })` flag).
 */

export type ConfirmDialogProps = {
  /** Heading + accessible name. */
  title: string;
  /** Body copy. Can be a string or rich content. */
  message: ReactNode;
  /** Confirm-button label. */
  confirmLabel?: string;
  /** Cancel-button label. */
  cancelLabel?: string;
  /** `true` ⇒ destructive style on the confirm button. */
  danger?: boolean;
  /** Disable confirm while an async caller is already submitting. */
  confirmDisabled?: boolean;
  /** Called when the user clicks confirm. */
  onConfirm: () => void;
  /** Called when the user clicks cancel / backdrop / Escape. */
  onClose: () => void;
  /** Optional `data-testid` for the dialog element. */
  "data-testid"?: string;
};

export function ConfirmDialog({
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = false,
  confirmDisabled = false,
  onConfirm,
  onClose,
  ...rest
}: ConfirmDialogProps) {
  useOneShotModal(onClose);

  // M7.5 review-fix P2: focus management for the alertdialog. On
  // mount, capture the previously focused element so we can restore
  // it on unmount, then move focus into the dialog (Cancel when
  // `danger`, Confirm otherwise — the safer default for destructive
  // actions). Tab is trapped between the two buttons.
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const initial = danger ? cancelBtnRef.current : confirmBtnRef.current;
    initial?.focus();
    return () => {
      try {
        prev?.focus();
      } catch {
        /* trigger may have been removed; ignore */
      }
    };
    // Only set up once per mount; reading `danger` once is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trap Tab inside the dialog so background controls can't be
  // reached while the alertdialog is open.
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;
    const cancel = cancelBtnRef.current;
    const confirm = confirmBtnRef.current;
    if (!cancel || !confirm) return;
    const active = document.activeElement;
    if (e.shiftKey) {
      if (active === cancel) {
        e.preventDefault();
        confirm.focus();
      }
    } else {
      if (active === confirm) {
        e.preventDefault();
        cancel.focus();
      }
    }
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[140] flex items-end justify-center bg-black/80 p-3 sm:items-center sm:p-4"
      role="alertdialog"
      aria-modal="true"
      aria-label={title}
      data-testid={rest["data-testid"]}
      data-confirm-dialog="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={onKeyDown}
    >
      <div className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-sm flex-col gap-3 overflow-y-auto rounded border border-rule bg-surface p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:p-5">
        <h2 className="text-base font-display tracking-tight text-ink">
          {title}
        </h2>
        <div className="text-sm text-ink-muted">{message}</div>
        <div className="mt-1 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onClose}
            data-testid="confirm-dialog-cancel"
            className="rounded border border-rule bg-canvas px-3 py-1.5 text-sm font-display uppercase tracking-wide text-ink transition-colors hover:bg-field focus:outline-none focus:ring-2 focus:ring-accent"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            onClick={() => {
              if (!confirmDisabled) onConfirm();
            }}
            disabled={confirmDisabled}
            data-testid="confirm-dialog-confirm"
            className={[
              "rounded px-3 py-1.5 text-sm font-display uppercase tracking-wide transition-colors focus:outline-none focus:ring-2 focus:ring-accent",
              danger
                ? "bg-destructive text-paper hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                : "bg-accent text-accent-ink hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60",
            ].join(" ")}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
